import { BusinessStreams } from '@cambridgeassessment/ciam-commons';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import env from 'src/env';
import type {
  AccountInfo,
  CambridgeProduct,
  NewOrgUser,
  OrgAppRoles,
  OrgAssociateCenter,
  OrgRoles,
  OrgUser,
  OrgUserDetails,
  SystemIDs,
} from 'src/types';
import { getJWT } from './gigya/gigya-service';

export const makeRequest = async <T, S = unknown>(
  { method, url, params, headers, ...rest }: AxiosRequestConfig,
  data?: S
): Promise<AxiosResponse<T>> => {
  const token = await getJWT();

  return axios({
    method,
    url,
    params,
    ...rest,
    headers: {
      Authorization: `Bearer ${token}`,
      ...headers,
    },
    data,
  });
};

type OrgUsersResponse = { users: OrgUser[]; nextCursorId: string | null };

export const getOrgUsers = async (
  bpid: string,
  params?: any
): Promise<OrgUser[]> => {
  const { data } = await makeRequest<OrgUsersResponse>({
    method: 'GET',
    url: `${env.ORG_USERS_BASE_URL}/organisation/${bpid}/users`,
    params,
  });

  const users = data.users;
  let cursorId = data.nextCursorId;

  while (cursorId) {
    const { data: cursorData } = await makeRequest<OrgUsersResponse>({
      method: 'GET',
      url: `${env.ORG_USERS_BASE_URL}/organisation/${bpid}/users`,
      params: {
        cursorId: cursorId,
      },
    });

    users.push(...cursorData.users);
    cursorId = cursorData.nextCursorId;
  }

  return users;
};

export const getOrgUserDetails = async (
  bpid: string,
  userId: string
): Promise<OrgUserDetails> => {
  const { data } = await makeRequest<OrgUserDetails>({
    method: 'GET',
    url: `${env.ORG_USERS_BASE_URL}/organisation/${bpid}/users/${userId}`,
  });

  return data;
};

export const deleteOrgUser = async (
  bpid: string,
  userId: string
): Promise<unknown> => {
  const { data } = await makeRequest<unknown>({
    method: 'DELETE',
    url: `${env.ORG_USERS_BASE_URL}/organisation/${bpid}/users/${userId}`,
  });

  return data;
};

export const updateOrgUser = async (
  bpid: string,
  userId: string,
  body: unknown
): Promise<unknown> => {
  const { data } = await makeRequest<unknown>(
    {
      method: 'PUT',
      url: `${env.ORG_USERS_BASE_URL}/organisation/${bpid}/users/${userId}`,
    },
    body
  );

  return data;
};

export const resetPassword = async (
  bpid: string,
  userId: string
): Promise<unknown> => {
  const { data } = await makeRequest<unknown>({
    method: 'POST',
    url: `${env.ORG_USERS_BASE_URL}/organisation/${bpid}/users/${userId}/reset_password`,
  });

  return data;
};

type InviteUsersPayload = {
  adminName: string;
  users: NewOrgUser[];
};

type ResendInvitationPayload = {
  adminName: string;
  user: OrgUser;
};

export const addOrgUser = async (
  bpid: string,
  userDetails: NewOrgUser
): Promise<unknown> => {
  const { data } = await makeRequest<unknown, InviteUsersPayload>(
    {
      method: 'POST',
      url: `${env.ORG_USERS_BASE_URL}/organisation/${bpid}/users`,
    },
    {
      adminName: 'Cambridge admin',
      users: [userDetails],
    }
  );

  return data;
};

export const getOrgRoles = async (
  bpid: string,
  uid: string
): Promise<OrgRoles> => {
  const { data } = await makeRequest<OrgRoles>({
    method: 'GET',
    url: `${env.ORG_USERS_BASE_URL}/organisation/${bpid}/roles/${uid}`,
  });

  return data;
};

export const getOrgAssociateCenters = async (
  bpid: string
): Promise<OrgAssociateCenter[]> => {
  const { data } = await makeRequest<OrgAssociateCenter[]>({
    method: 'GET',
    url: `${env.ORG_USERS_BASE_URL}/organisation/${bpid}/associateCenters`,
  });

  return data;
};

export const getUserDetails = async (): Promise<AccountInfo> => {
  const { data } = await makeRequest<any>({
    method: 'GET',
    url: `${env.ORG_USERS_BASE_URL}/users`,
  });

  return data;
};

export const updateOrgUserExaminerId = async (
  bpid: string,
  userId: string,
  systemIDs: SystemIDs[]
): Promise<unknown> => {
  const { data } = await makeRequest<unknown>(
    {
      method: 'PATCH',
      url: `${env.ORG_USERS_BASE_URL}/organisation/${bpid}/users/${userId}/examiner-id`,
    },
    systemIDs
  );
  return data;
};

export const updateOrgUserCreditorNumber = async (
  bpid: string,
  userId: string,
  creditorNumber: string
): Promise<unknown> => {
  const { data } = await makeRequest<unknown>({
    method: 'PATCH',
    url: `${env.ORG_USERS_BASE_URL}/organisation/${bpid}/users/${userId}/creditor-number/${creditorNumber}`,
  });

  return data;
};

export const updateOrgUserBusinessStream = async (
  bpid: string,
  userId: string,
  businessStream: BusinessStreams[]
): Promise<unknown> => {
  const { data } = await makeRequest<unknown>(
    {
      method: 'PATCH',
      url: `${env.ORG_USERS_BASE_URL}/organisation/${bpid}/users/${userId}/business-stream`,
    },
    businessStream
  );

  return data;
};

export const deleteOrgUserCreditorNumber = async (
  bpid: string,
  userId: string
): Promise<unknown> => {
  const { data } = await makeRequest<unknown>({
    method: 'DELETE',
    url: `${env.ORG_USERS_BASE_URL}/organisation/${bpid}/users/${userId}/creditor-number`,
  });

  return data;
};

export const deleteOrgUserBusinessStream = async (
  bpid: string,
  userId: string
): Promise<unknown> => {
  const { data } = await makeRequest<unknown>(
    {
      method: 'DELETE',
      url: `${env.ORG_USERS_BASE_URL}/organisation/${bpid}/users/${userId}/business-stream`,
    },
    []
  );

  return data;
};

export const getOrgAppRoles = async (
  bpid: string,
  uid: string
): Promise<OrgAppRoles> => {
  const { data } = await makeRequest<OrgAppRoles>({
    method: 'GET',
    url: `${env.ORG_USERS_BASE_URL}/organisation/${bpid}/app-roles/${uid}`,
  });

  return data;
};

export const getPlatformTiles = async (
  bpid: string
): Promise<CambridgeProduct[]> => {
  const { data } = await makeRequest<CambridgeProduct[]>({
    method: 'GET',
    url: `${env.ORG_USERS_BASE_URL}/organisation/${bpid}/platform-tiles`,
  });

  return data;
};

export const getPlatformTilesForB2C = async (): Promise<CambridgeProduct[]> => {
  const { data } = await makeRequest<CambridgeProduct[]>({
    method: 'GET',
    url: `${env.ORG_USERS_BASE_URL}/platform-tiles`,
  });

  return data;
};

export const resendInvitation = async (
  userDetails: OrgUser
): Promise<unknown> => {
  const { data } = await makeRequest<unknown, ResendInvitationPayload>(
    {
      method: 'POST',
      url: `${env.ORG_USERS_BASE_URL}/organisation/resend-invitation`,
    },
    {
      adminName: 'Cambridge admin',
      user: userDetails,
    }
  );

  return data;
};
